import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { Button, Card, Form, Input, Select, Space, Tag, Typography } from "antd";
import { PageAlert as Alert } from "@/views/components/PageAlert";
import type { ComponentProps, ComponentType } from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { InputNumber as ZhInputNumber, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { enterpriseManageService } from "@/api/services/enterpriseManageService";
import { prodTypeService } from "@/api/services/prodTypeService";
import { clearDaiBanState, dateTimeFormat, getDaiBanState } from "@/utils";
import { getEnterpriseName } from "@/utils/customer";
import { useUserStore } from "@/views/stores";
import { TableImageCell } from "@/views/components/TableImageCell";
import { TableSearchIcon } from "@/views/components/TableSearchIcon";
import { formatCurrency } from "@zhihaoscm/utils";
import AssignDialog, { type assignSubmitData, type Officer } from "@/views/components/AssignDialog/AssignDialog";
import type { BargainPageResult, BargainRow, ConsignmentQuery } from "./types";

type BargainListQuery = Omit<ConsignmentQuery, "publishState"> & {
    bargainState?: number;
    publishState?: number[];
};
type BargainFilterOption = { label: string; value: number | string };

const defaultQuery: BargainListQuery = { page: 1, size: 10 };

// beta.13 的运行时支持 controls 配置，类型声明暂未暴露该兼容字段。
type ProductWeightInputProps = ComponentProps<typeof ZhInputNumber> & { controls?: boolean };
const ProductWeightInput = ZhInputNumber as ComponentType<ProductWeightInputProps>;
const bargainStateLabels: Record<number, string> = { 1: "待开始", 2: "捡漏中", 3: "已完成" };
const productStateLabels: Record<number, string> = { 1: "上架", 2: "下架" };
const publishStateLabels: Record<number, string> = {
    1: "待审核",
    2: "未通过",
    3: "已发布",
    4: "已下架",
    5: "已关闭",
    6: "待提交"
};

type BargainFilterValues = {
    keyword?: string;
    productTypeId?: string;
    supplierId?: number;
    bargainState?: number;
    state?: number;
    publishState?: number[];
    handlerName?: string;
};

type BargainSpecialist = {
    user: { personId?: number; name?: string; employeeId?: string };
    productCount?: number;
};

export interface BargainPageProps {
    assignBargain?: (id: number | string, handlerId: number) => Promise<void>;
    closeBargain?: (id: number | string, unActiveFileIds?: number[]) => Promise<void>;
    downShelfBargain?: (id: number | string) => Promise<void>;
    findServiceSpecials?: (keyword: string) => Promise<BargainSpecialist[]>;
    loadBargains?: (query: BargainListQuery) => Promise<BargainPageResult>;
    loadProductTypeOptions?: (keyword: string) => Promise<BargainFilterOption[]>;
    loadSupplierOptions?: (keyword: string) => Promise<BargainFilterOption[]>;
    updateWeight?: (params: { id: number | string; weight: number }) => Promise<void>;
    permissions?: string[];
}

/** 复用 Vue 捡漏商品分页接口。 */
export async function loadBargains(query: BargainListQuery): Promise<BargainPageResult> {
    const response = await request.service.get<BargainPageResult>("/admin/product-bargain/paging", { params: query });
    return response.data;
}

/** 复用 Vue SelectRemote 的捡漏品类候选接口。 */
export async function loadBargainProductTypeOptions(keyword: string): Promise<BargainFilterOption[]> {
    const rows = await prodTypeService.selectorInProduct({ keyword: keyword || undefined, productType: 3 });
    return rows.map(row => ({
        label: row.areaAbbreviation ? `${row.name} | ${row.areaAbbreviation}` : row.name,
        value: String(row.id)
    }));
}

/** 复用 Vue SelectRemote 的捡漏供应商候选接口。 */
export async function loadBargainSupplierOptions(keyword: string): Promise<BargainFilterOption[]> {
    const rows = await enterpriseManageService.selectorByProduct({ name: keyword || undefined, productType: 3 });
    return rows.map(row => ({ label: row.supplierName || "--", value: Number(row.id) }));
}

/** 读取待办带入的发布状态，保持 Vue init-param 的首次查询。 */
function getInitialBargainQuery(): BargainListQuery {
    const publishState = getDaiBanState("bargainPublishState").map(Number).filter(Number.isFinite);
    return publishState.length ? { ...defaultQuery, publishState } : defaultQuery;
}

/** 复用 Vue 捡漏商品权重更新接口。 */
export async function updateBargainWeight(params: { id: number | string; weight: number }): Promise<void> {
    await request.service.put(`/admin/product-bargain/weight/${params.id}`, params);
}

/** 复用 Vue 捡漏商品下架接口。 */
export async function downShelfBargain(id: number | string): Promise<void> {
    await request.service.put(`/admin/product-bargain/down-shelf/${id}`);
}

/** 复用 Vue 捡漏商品关闭接口，并提交需要解除关联的文件 ID。 */
export async function closeBargain(id: number | string, unActiveFileIds: number[] = []): Promise<void> {
    await request.service.put(`/admin/product-bargain/close/${id}`, { unActiveFileIds });
}

/** 复用 Vue 捡漏专员候选查询接口。 */
export async function findServiceSpecials(keyword: string): Promise<BargainSpecialist[]> {
    const response = await request.service.get<BargainSpecialist[]>(
        `/admin/product-bargain/find/specials?name=${encodeURIComponent(keyword)}`
    );
    return response.data;
}

/** 复用 Vue 捡漏商品指派接口。 */
export async function assignBargain(id: number | string, handlerId: number): Promise<void> {
    await request.service.get(`/admin/product-bargain/assign/${id}/${handlerId}`);
}

/** 将 Vue 捡漏商品列表返回的捡漏、商品和发布状态枚举统一为数字。 */
function normalizeBargainRows(rows?: BargainRow[]): BargainRow[] {
    return (rows || []).map(row => ({
        ...row,
        productBargain: {
            ...row.productBargain,
            bargainState: row.productBargain.bargainState == null ? undefined : Number(row.productBargain.bargainState),
            state: row.productBargain.state == null ? undefined : Number(row.productBargain.state),
            publishState: row.productBargain.publishState == null ? undefined : Number(row.productBargain.publishState)
        }
    }));
}

/** 捡漏商品列表，完整保留 Vue 筛选、列、权限和复制入口。 */
export function BargainPage({
    assignBargain: assign = assignBargain,
    closeBargain: close = closeBargain,
    downShelfBargain: removeFromShelf = downShelfBargain,
    findServiceSpecials: findSpecials = findServiceSpecials,
    loadBargains: load = loadBargains,
    loadProductTypeOptions: loadProductTypes = loadBargainProductTypeOptions,
    loadSupplierOptions: loadSuppliers = loadBargainSupplierOptions,
    updateWeight: saveWeight = updateBargainWeight,
    permissions
}: BargainPageProps) {
    const navigate = useNavigate();
    const storePermissions = useUserStore(state => state.userInfo.permissions || []);
    const banButton = useUserStore(state => state.banButton);
    const currentPermissions = permissions || storePermissions;
    const canDeal = currentPermissions.includes("ROLE_PRODUCT_DEAL");
    const canManage = currentPermissions.includes("ROLE_PRODUCT_MANAGE");
    const canClosePermission = currentPermissions.includes("ROLE_PRODUCT_CLOSE");
    const [items, setItems] = useState<BargainRow[]>([]);
    const [total, setTotal] = useState(0);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [assignTarget, setAssignTarget] = useState<BargainRow | null>(null);
    const [specialists, setSpecialists] = useState<BargainSpecialist[]>([]);
    const [selectedHandlerId, setSelectedHandlerId] = useState<number | undefined>();
    const [assignError, setAssignError] = useState<string | null>(null);
    const [assignLoading, setAssignLoading] = useState(false);
    const [query, setQuery] = useState<BargainListQuery>(getInitialBargainQuery);
    const [editingWeightId, setEditingWeightId] = useState<number | string | null>(null);
    const [editingWeightValue, setEditingWeightValue] = useState<number | string>("");
    const [filterForm] = Form.useForm<BargainFilterValues>();
    const [productTypeOptions, setProductTypeOptions] = useState<BargainFilterOption[]>([]);
    const [productTypeLoading, setProductTypeLoading] = useState(false);
    const [supplierOptions, setSupplierOptions] = useState<BargainFilterOption[]>([]);
    const [supplierLoading, setSupplierLoading] = useState(false);

    useEffect(() => {
        let disposed = false;
        void load(query)
            .then(result => {
                if (!disposed) {
                    setErrorMessage(null);
                    setItems(normalizeBargainRows(result.list));
                    setTotal(result.total || 0);
                }
            })
            .catch(() => {
                if (!disposed) {
                    setErrorMessage("捡漏商品加载失败");
                    setItems([]);
                    setTotal(0);
                }
            });
        return () => {
            disposed = true;
        };
    }, [load, query]);

    // 将旧 Vue 七项筛选转换为捡漏分页接口参数，并同步清理待办状态。
    const submitFilters = (values: BargainFilterValues) => {
        const publishState = values.publishState?.map(Number).filter(Number.isFinite);
        clearDaiBanState(publishState || [], "bargainPublishState");
        setErrorMessage(null);
        setQuery({
            ...defaultQuery,
            ...(values.keyword?.trim() ? { keyword: values.keyword.trim() } : {}),
            ...(values.productTypeId ? { productTypeId: values.productTypeId } : {}),
            ...(values.supplierId ? { supplierId: Number(values.supplierId) } : {}),
            ...(values.bargainState ? { bargainState: Number(values.bargainState) } : {}),
            ...(values.state ? { state: Number(values.state) } : {}),
            ...(publishState?.length ? { publishState } : {}),
            ...(values.handlerName?.trim() ? { handlerName: values.handlerName.trim() } : {})
        });
    };

    // 清空全部筛选和待办缓存，并恢复旧 Vue 默认分页参数。
    const resetFilters = () => {
        clearDaiBanState([], "bargainPublishState");
        filterForm.resetFields();
        filterForm.setFieldValue("publishState", []);
        setProductTypeOptions([]);
        setSupplierOptions([]);
        setQuery(defaultQuery);
    };

    /** 按 Vue SelectRemote 契约查询捡漏品类候选。 */
    const searchProductTypes = async (keyword: string) => {
        setProductTypeLoading(true);
        try {
            setProductTypeOptions(await loadProductTypes(keyword.trim()));
        } catch {
            setProductTypeOptions([]);
        } finally {
            setProductTypeLoading(false);
        }
    };

    /** 按 Vue SelectRemote 契约查询捡漏供应商候选。 */
    const searchSuppliers = async (keyword: string) => {
        setSupplierLoading(true);
        try {
            setSupplierOptions(await loadSuppliers(keyword.trim()));
        } catch {
            setSupplierOptions([]);
        } finally {
            setSupplierLoading(false);
        }
    };

    // 状态动作成功后沿用当前 Vue 分页参数重新加载列表。
    const refresh = async () => {
        const result = await load(query);
        setItems(normalizeBargainRows(result.list));
        setTotal(result.total || 0);
    };

    const handleClose = async (row: BargainRow) => {
        const id = row.productBargain.id;
        if (id == null) return;
        // Vue 关闭接口要求清理主图、视频和附加图片的文件 ID。
        const unActiveFileIds = [
            row.mainFile?.id,
            row.videoFile?.id,
            ...(row.otherImgFile || []).map(file => file.id)
        ].filter((fileId): fileId is number => fileId != null);
        try {
            await close(id, unActiveFileIds);
            await refresh();
        } catch {
            // 关闭接口或刷新失败时保留当前捡漏商品列表，并反馈 Vue 页面同类操作错误。
            setErrorMessage("捡漏商品关闭失败");
        }
    };

    const handleDownShelf = async (id?: number | string) => {
        if (id == null) return;
        try {
            await removeFromShelf(id);
            await refresh();
        } catch {
            // 下架接口或刷新失败时保留当前捡漏商品列表，并反馈 Vue 页面同类操作错误。
            setErrorMessage("捡漏商品下架失败");
        }
    };

    const searchSpecialists = async (keyword: string) => {
        setAssignLoading(true);
        try {
            setAssignError(null);
            const nextSpecialists = await findSpecials(keyword);
            setSpecialists(nextSpecialists);
            return nextSpecialists;
        } catch {
            setAssignError("专员查询失败");
            setSpecialists([]);
        } finally {
            setAssignLoading(false);
        }
    };

    const openAssign = (row: BargainRow) => {
        setAssignTarget(row);
        setSelectedHandlerId(row.productBargain.handlerId);
        void searchSpecialists("");
    };

    const handleAssign = async ({ officerId }: assignSubmitData) => {
        if (!assignTarget || officerId == null) {
            setAssignError("请选择指派的服务人员");
            return;
        }
        const id = assignTarget.productBargain.id;
        if (id == null) return;
        setAssignLoading(true);
        try {
            await assign(id, officerId);
            setAssignTarget(null);
            await refresh();
        } catch (error) {
            setAssignError("变更指派失败");
            throw error;
        } finally {
            setAssignLoading(false);
        }
    };

    /** 将 Vue 四个自定义排序列映射为捡漏分页接口字段。 */
    const handleSortChange = useCallback(
        ({ prop, order }: { prop?: string; order: "ascending" | "descending" | null }) => {
            const sortKeyMap: Record<string, string> = {
                "productBargain.weight": "weight",
                bargainStartTime: "bargain_start_time",
                bargainEndTime: "bargain_end_time",
                updatedTime: "updated_time"
            };
            if (!prop || !sortKeyMap[prop]) return;
            setQuery(currentQuery => {
                const nextQuery = { ...currentQuery, page: 1 };
                if (!order) {
                    delete nextQuery.sortKey;
                    delete nextQuery.sortOrder;
                } else {
                    nextQuery.sortKey = sortKeyMap[prop];
                    nextQuery.sortOrder = order === "ascending" ? "asc" : "desc";
                }
                return nextQuery;
            });
        },
        []
    );

    // 用 zhihao-ui-react 列契约承接 Vue 捡漏商品的嵌套字段、状态枚举和权限动作。
    const columns: TableColumnProps<BargainRow>[] = [
        {
            key: "id",
            label: "商品编号",
            width: 176,
            prop: "productBargain.id",
            render: ({ row }) => (
                <Button
                    aria-label="查看捡漏商品详情"
                    type="link"
                    onClick={() =>
                        void navigate(
                            `/productManagement/bargain/detail?id=${row.productBargain.id || ""}&time=${Date.now()}`
                        )
                    }
                >
                    {row.productBargain.id || "--"}
                </Button>
            )
        },
        {
            key: "weight",
            label: "权重值",
            width: 176,
            prop: "productBargain.weight",
            sortable: true,
            render: ({ row }) => {
                const id = row.productBargain.id;
                if (id != null && editingWeightId === id) {
                    return (
                        <Space.Compact className="admin-table-inline-editor">
                            <ProductWeightInput
                                aria-label="捡漏商品权重值"
                                autoFocus
                                controls={false}
                                max={10000}
                                min={0}
                                precision={0}
                                value={editingWeightValue}
                                onChange={setEditingWeightValue}
                            />
                            <Button
                                aria-label="保存捡漏商品权重"
                                size="small"
                                type="link"
                                onClick={() => {
                                    const weight = Number(editingWeightValue);
                                    if (editingWeightValue === "" || !Number.isFinite(weight)) return;
                                    setEditingWeightId(null);
                                    void saveWeight({ id, weight });
                                }}
                            >
                                保存
                            </Button>
                            <Button
                                aria-label="取消捡漏商品权重"
                                size="small"
                                type="link"
                                onClick={() => setEditingWeightId(null)}
                            >
                                取消
                            </Button>
                        </Space.Compact>
                    );
                }
                return (
                    <Space size={4}>
                        <span>{row.productBargain.weight ?? 0}</span>
                        {id != null ? (
                            <Button
                                aria-label="编辑捡漏商品权重值"
                                icon={<i aria-hidden="true" className="iconfont icon-edit" />}
                                size="small"
                                type="link"
                                onClick={() => {
                                    setEditingWeightValue(row.productBargain.weight ?? 0);
                                    setEditingWeightId(id);
                                }}
                            />
                        ) : null}
                    </Space>
                );
            }
        },
        {
            key: "title",
            label: "主图/标题",
            minWidth: 176,
            prop: "imgMainId",
            render: ({ row }) => (
                <TableImageCell alt="捡漏商品主图" src={row.mainFile?.path} title={row.productBargain.title || "--"} />
            )
        },
        {
            key: "productType",
            label: "品类",
            minWidth: 120,
            prop: "productTypeId",
            render: ({ row }) =>
                row.productType?.name
                    ? `${row.productType.name}${row.productType.areaAbbreviation ? ` (${row.productType.areaAbbreviation})` : ""}`
                    : "--"
        },
        {
            key: "supplier",
            label: "供应商",
            minWidth: 120,
            prop: "supplierId",
            render: ({ row }) =>
                row.productBargain.supplierEnterprise?.enterpriseName ||
                row.productBargain.supplierEnterprise?.supplierName ||
                getEnterpriseName(row.productBargain.supplierEnterprise as never)
        },
        {
            key: "bargainUnitPrice",
            label: "捡漏单价（元/吨)",
            minWidth: 148,
            prop: "bargainUnitPrice",
            align: "right",
            headerAlign: "right",
            showOverflowTooltip: false,
            render: ({ row }) => (
                <div className="flex flex-col items-end">
                    <span>
                        {row.productBargain.bargainUnitPrice
                            ? formatCurrency(row.productBargain.bargainUnitPrice)
                            : "--"}
                    </span>
                    <span className="text-xs text-gray-400 line-through">
                        {row.productBargain.unitPrice ? formatCurrency(row.productBargain.unitPrice) : ""}
                    </span>
                </div>
            )
        },
        {
            key: "bargainTon",
            label: "捡漏吨数 (吨)",
            width: 120,
            prop: "productBargain.bargainTon",
            align: "right",
            headerAlign: "right",
            render: ({ row }) => (row.productBargain.bargainTon ? formatCurrency(row.productBargain.bargainTon) : "--")
        },
        {
            key: "totalCost",
            label: "捡漏总价（元）",
            width: 148,
            prop: "totalCost",
            align: "right",
            headerAlign: "right",
            render: ({ row }) => (row.productBargain.totalCost ? formatCurrency(row.productBargain.totalCost) : "--")
        },
        {
            key: "bargainStartTime",
            label: "捡漏开始时间",
            width: 150,
            prop: "bargainStartTime",
            sortable: true,
            render: ({ row }) =>
                row.productBargain.bargainStartTime ? dateTimeFormat(row.productBargain.bargainStartTime) : "--"
        },
        {
            key: "bargainEndTime",
            label: "捡漏截止时间",
            width: 150,
            prop: "bargainEndTime",
            sortable: true,
            render: ({ row }) =>
                row.productBargain.bargainEndTime ? dateTimeFormat(row.productBargain.bargainEndTime) : "--"
        },
        {
            key: "handlerName",
            label: "商品专员",
            width: 100,
            prop: "productBargain.handlerName",
            render: ({ row }) => row.productBargain.handlerName || "--"
        },
        {
            key: "updatedTime",
            label: "更新时间",
            width: 150,
            prop: "updatedTime",
            sortable: true,
            render: ({ row }) => dateTimeFormat(row.productBargain.updatedTime)
        },
        {
            key: "bargainState",
            label: "捡漏状态",
            width: 90,
            prop: "productBargain.bargainState",
            render: ({ row }) => <Tag>{bargainStateLabels[row.productBargain.bargainState || 0] || "--"}</Tag>
        },
        {
            key: "state",
            label: "上架状态",
            width: 90,
            prop: "productBargain.state",
            render: ({ row }) => <Tag>{productStateLabels[row.productBargain.state || 0] || "--"}</Tag>
        },
        {
            key: "publishState",
            label: "发布状态",
            width: 90,
            prop: "productBargain.publishState",
            render: ({ row }) => <Tag>{publishStateLabels[row.productBargain.publishState || 0] || "--"}</Tag>
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 230,
            prop: "operation",
            render: ({ row }) => {
                const { bargainState, id, publishState, state } = row.productBargain;
                const showAudit = publishState === 1;
                const showAssign = !(state === 2 && [4, 5].includes(publishState || 0));
                const showEdit = state === 2 && [1, 2, 4, 6].includes(publishState || 0);
                const showDown = state === 1 && [2, 3].includes(publishState || 0);
                const showClose = state === 2 && [1, 2, 4, 6].includes(publishState || 0);
                const showCopy = publishState === 5 && state === 2 && bargainState === 3;

                return (
                    <Space wrap>
                        {canManage && showAssign ? (
                            <Button
                                aria-label="变更指派"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() => openAssign(row)}
                            >
                                变更指派
                            </Button>
                        ) : null}
                        {canManage && showAudit ? (
                            <Button
                                aria-label="审核捡漏商品"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() =>
                                    void navigate(`/productManagement/bargain/audit?id=${id || ""}&time=${Date.now()}`)
                                }
                            >
                                审核
                            </Button>
                        ) : null}
                        {canDeal && showEdit ? (
                            <Button
                                aria-label="编辑捡漏商品"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() =>
                                    void navigate(`/productManagement/bargain/edit?id=${id || ""}&time=${Date.now()}`)
                                }
                            >
                                编辑
                            </Button>
                        ) : null}
                        {canClosePermission && showClose ? (
                            <Button
                                aria-label="关闭捡漏商品"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() => void handleClose(row)}
                            >
                                关闭
                            </Button>
                        ) : null}
                        {canDeal && showDown ? (
                            <Button
                                aria-label="下架捡漏商品"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() => void handleDownShelf(id)}
                            >
                                下架
                            </Button>
                        ) : null}
                        {canDeal && showCopy ? (
                            <Button
                                aria-label="复制捡漏商品"
                                disabled={banButton}
                                size="small"
                                type="link"
                                onClick={() =>
                                    void navigate(
                                        `/productManagement/bargain/add?copyId=${id || ""}&time=${Date.now()}`
                                    )
                                }
                            >
                                复制
                            </Button>
                        ) : null}
                    </Space>
                );
            }
        }
    ];

    return (
        <section aria-label="bargain-page" className="flex h-full min-h-full flex-col">
            <AssignDialog
                open={Boolean(assignTarget)}
                info={assignTarget || undefined}
                title="变更指派商品专员"
                confirmText="确认"
                officers={specialists as Officer[]}
                searchSpecial={searchSpecialists}
                tips="跟进的商品数量"
                countFiled="productCount"
                countSuffix="个"
                curOfficerId={selectedHandlerId}
                loading={assignLoading}
                error={assignError}
                onClose={() => {
                    setAssignTarget(null);
                    setAssignError(null);
                }}
                onCurOfficerIdChange={setSelectedHandlerId}
                onSubmit={handleAssign}
            />
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<BargainRow>
                    data={items}
                    columns={columns}
                    onSortChange={handleSortChange}
                    pagination={false}
                    rowKey={row => String(row.productBargain.id)}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                捡漏商品管理
                            </Typography.Title>

                            {errorMessage ? (
                                <Alert className="mb-4" message={errorMessage} role="alert" type="error" />
                            ) : null}

                            <Form
                                className="admin-table-filter-form mb-4"
                                form={filterForm}
                                initialValues={{ publishState: query.publishState || [] }}
                                layout="inline"
                                onFinish={values => submitFilters(values)}
                            >
                                {canDeal ? (
                                    <Button
                                        aria-label="新增捡漏商品"
                                        className="admin-table-primary-action"
                                        disabled={banButton}
                                        type="primary"
                                        onClick={() =>
                                            void navigate(`/productManagement/bargain/add?time=${Date.now()}`)
                                        }
                                    >
                                        新增商品
                                    </Button>
                                ) : null}
                                <Form.Item className="product-filter-keyword" name="keyword">
                                    <Input placeholder="请输入商品编号或商品标题" />
                                </Form.Item>
                                <Form.Item className="product-filter-category" name="productTypeId">
                                    <Select
                                        allowClear
                                        aria-label="捡漏品类"
                                        filterOption={false}
                                        loading={productTypeLoading}
                                        options={productTypeOptions}
                                        placeholder="请选择品类"
                                        showSearch
                                        onSearch={value => void searchProductTypes(value)}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-supplier" name="supplierId">
                                    <Select
                                        allowClear
                                        aria-label="捡漏供应商"
                                        filterOption={false}
                                        loading={supplierLoading}
                                        options={supplierOptions}
                                        placeholder="请选择供应商"
                                        showSearch
                                        onSearch={value => void searchSuppliers(value)}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-state" name="bargainState">
                                    <Select
                                        allowClear
                                        placeholder="请选择捡漏状态"
                                        options={Object.entries(bargainStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-state" name="state">
                                    <Select
                                        allowClear
                                        placeholder="请选择上架状态"
                                        options={Object.entries(productStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-publish-state" name="publishState">
                                    <Select
                                        allowClear
                                        mode="multiple"
                                        placeholder="请选择发布状态"
                                        options={Object.entries(publishStateLabels).map(([value, label]) => ({
                                            label,
                                            value: Number(value)
                                        }))}
                                        onChange={values => clearDaiBanState(values, "bargainPublishState")}
                                    />
                                </Form.Item>
                                <Form.Item className="product-filter-handler" name="handlerName">
                                    <Input placeholder="请输入商品专员搜索" />
                                </Form.Item>
                                <Button
                                    aria-label="查询商品"
                                    htmlType="submit"
                                    icon={<TableSearchIcon name="search" />}
                                    type="primary"
                                >
                                    搜索
                                </Button>
                                <Button
                                    aria-label="重置"
                                    className="table-filter-reset-button"
                                    icon={<TableSearchIcon name="reset" />}
                                    title="重置"
                                    onClick={resetFilters}
                                />
                            </Form>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                showTotal={value => `共${value}条数据`}
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                // 沿用 Vue 捡漏商品分页契约，切换页大小后回到第 1 页。
                                onChange={(page, pageSize) =>
                                    setQuery(currentQuery => ({
                                        ...currentQuery,
                                        page: pageSize !== currentQuery.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default BargainPage;
